import type { State } from 'src/store/types.js';
import type { MeetingsPageMeeting, MeetingsPageViewModel } from 'src/pages/meetings-page/meetings-page-view-model.js';
import { key, type MeetingModel } from 'src/store/selectors';
import { meetings, now } from 'src/store/selectors';
import { orderBy } from 'lodash';
import { LocalDate } from 'src/utilities/local-date';
import { calculateUpdateStatus } from 'src/models/resolvers/calculate-update-status';
import type { AbstractPageView } from 'src/pages/abstract-page-view';
import { tasksNotDeletedWithFunctionName } from '../../store/selectors/responsibilities/tasks-not-deleted-with-function-name';
import { getUsersForAccess } from '../get-users-for-access';
import { getOrganization } from '../../store/selectors/organization/getOrganization';
import { writeAccess } from '../../store/selectors/user/writeAccess';
import { currentEmployeeUuid } from '../../store/selectors/employees/current-employee-uuid';

function toMeeting(m: MeetingModel, today: LocalDate, hrefPrefix: string): MeetingsPageMeeting {
  let name = m.name;
  if (m.type === 'INTERVIEW_TERMINATED' || m.type === 'INTERVIEW_ACTIVE') {
    name = m.interviewEmployeeName;
  }
  const result = {
    uuid: m.uuid,
    name,
    suggested: m.type === 'SUGGESTED',
    prevDate: '',
    prevText: '',
    prevClassified: false,
    prevRestricted: false,
    prevHref: '',
    nextDate: '',
    nextText: '',
    nextClassified: false,
    nextRestricted: false,
    nextHref: '',
    currentUserHasReadAccess: true, // TODO
    updateStatus: calculateUpdateStatus(m, today), // TODO
    occurrences: m.meetingOccurrences,
    employeeUuid:
      m.type === 'INTERVIEW_TERMINATED' || m.type === 'INTERVIEW_ACTIVE' ? m.interviewEmployeeUuid : undefined,
    meetingUuid: m.type === 'INTERVIEW_TERMINATED' || m.type === 'INTERVIEW_ACTIVE' ? undefined : m.meetingUuid,
  };
  const previousOccurrences = m.meetingOccurrences.filter((e) => LocalDate.fromString(e.date).isBefore(today));
  const prevOccurrence = orderBy(previousOccurrences, [(o) => o.date], ['desc'])[0];
  if (prevOccurrence !== undefined) {
    let prevHref = prevOccurrence.restricted ? '' : hrefPrefix + 'meetingOccurrences/' + prevOccurrence.uuid;
    if (prevHref && !prevOccurrence.isConfirmedEntity) {
      prevHref += '?edit';
    }
    result.prevDate = prevOccurrence.date;
    result.prevText = LocalDate.fromString(prevOccurrence.date).toStringForDisplayWithYear();
    result.prevClassified = prevOccurrence.classified;
    result.prevRestricted = prevOccurrence.restricted;
    result.prevHref = prevHref;
  }
  const nextOccurrences = m.meetingOccurrences.filter((e) => LocalDate.fromString(e.date).isSameOrAfter(today));
  const nextOccurrence = orderBy(nextOccurrences, [(o) => o.date], ['asc'])[0];
  if (nextOccurrence !== undefined) {
    let nextHref = nextOccurrence.restricted ? '' : hrefPrefix + 'meetingOccurrences/' + nextOccurrence.uuid;
    if (nextHref && !nextOccurrence.isConfirmedEntity) {
      nextHref += '?edit';
    }
    result.nextDate = nextOccurrence.date;
    result.nextText = LocalDate.fromString(nextOccurrence.date).toStringForDisplayWithYear();
    result.nextClassified = nextOccurrence.classified;
    result.nextRestricted = nextOccurrence.restricted;
    result.nextHref = nextHref;
  }
  if (m.type === 'INTERVIEW_ACTIVE') console.log('INTERVIEW_ACTIVE', m, result);
  return result;
}

function toMeetings(m: MeetingModel[], today: LocalDate, hrefPrefix: string): MeetingsPageMeeting[] {
  return m.map((x) => toMeeting(x, today, hrefPrefix));
}

export function meetingsPageView(hrefPrefix: string, viewModel: AbstractPageView, state: State): MeetingsPageViewModel {
  const meetings1 = meetings(state);
  const futureMeetings = meetings1.filter((meeting) => {
    return meeting.type === 'FUTURE';
  });
  const pastMeetings = meetings1.filter((meeting) => {
    return meeting.type === 'PAST';
  });
  const suggestedMeetings = meetings1.filter((meeting) => {
    return meeting.type === 'SUGGESTED';
  });

  // TEMPORARY: Filter employee interviews based on PERFORMANCE_REVIEW feature and access
  // TODO: Remove this override once the server properly handles PERFORMANCE_REVIEW
  const organization = getOrganization(state);
  const hasPerformanceReview = organization?.features?.includes('PERFORMANCE_REVIEW');
  const hasMeetings = organization?.features?.includes('MEETINGS');
  const currentUserHasWriteAccess = writeAccess(state);
  const currentEmployeeId = currentEmployeeUuid(state);
  const usersWithAccess = getUsersForAccess(state);

  let activeEmployeeMeetings = meetings1.filter((meeting) => {
    return meeting.type === 'INTERVIEW_ACTIVE';
  });
  let terminatedEmployeeMeetings = meetings1.filter((meeting) => {
    return meeting.type === 'INTERVIEW_TERMINATED' && meeting.meetingOccurrences.length > 0;
  });

  // Apply filtering for PERFORMANCE_REVIEW-only accounts
  if (hasPerformanceReview && !hasMeetings) {
    if (currentUserHasWriteAccess) {
      // Show interviews for employees who do NOT have write access
      // Write access = ADMIN or USER, so we show interviews for READONLY and NONE
      const employeesWithWriteAccess = usersWithAccess
        .filter((u) => u.accessLevel === 'ADMIN' || u.accessLevel === 'USER')
        .map((u) => u.uuid);

      activeEmployeeMeetings = activeEmployeeMeetings.filter((meeting) => {
        return !employeesWithWriteAccess.includes(meeting.interviewEmployeeUuid);
      });
      terminatedEmployeeMeetings = terminatedEmployeeMeetings.filter((meeting) => {
        return !employeesWithWriteAccess.includes(meeting.interviewEmployeeUuid);
      });
    } else {
      // User has read-only access - show only their own interview
      activeEmployeeMeetings = activeEmployeeMeetings.filter((meeting) => {
        return meeting.interviewEmployeeUuid === currentEmployeeId;
      });
      terminatedEmployeeMeetings = terminatedEmployeeMeetings.filter((meeting) => {
        return meeting.interviewEmployeeUuid === currentEmployeeId;
      });
    }
  }

  const today = now(state);
  return {
    ...viewModel,
    type: 'meetings-page',
    href: hrefPrefix,
    icon: 'meetings',
    today: LocalDate.now(),
    key: key(state),
    tasks: tasksNotDeletedWithFunctionName(state),
    futureMeetings: orderBy(toMeetings(futureMeetings, today, hrefPrefix), ['nextDate', 'uuid'], ['asc']),
    pastMeetings: orderBy(toMeetings(pastMeetings, today, hrefPrefix), ['prevDate', 'uuid'], ['desc']),
    suggestedMeetings: orderBy(
      toMeetings(suggestedMeetings, today, hrefPrefix),
      [(e) => e.name.toLowerCase(), 'uuid'],
      ['asc'],
    ),
    activeEmployeeInterviews: orderBy(
      toMeetings(activeEmployeeMeetings, today, hrefPrefix),
      [(e) => e.name.toLowerCase(), 'uuid'],
      ['asc'],
    ),
    terminatedEmployeeInterviews: orderBy(
      toMeetings(terminatedEmployeeMeetings, today, hrefPrefix),
      [(e) => e.name.toLowerCase(), 'uuid'],
      ['asc'],
    ),
    userEmails: getUsersForAccess(state)
      .map((e) => e.email ?? '')
      .filter((x) => x !== ''),
  };
}
